import React, { useState, useRef } from 'react';
import debounce from 'lodash/debounce';

import { FieldInput } from '@magoosh/lib/formik_inputs/field_input/template';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';
import { formatPrice } from 'utilities/money';

import { PaymentStyle, SubscriptionFormValues } from '@payments/types';

import style from './style.module.scss';

interface Props {
  values: SubscriptionFormValues;
  formattedexpirationDate: string;
  formattedPrice: string;
  formattedDiscountPrice: string;
  formattedInstallmentPrice: string;
  showPlanName: boolean;
  setFieldValue: (field: string, value: any) => void;
}

/**
 * For displaying and editing the total for the purchase of a single subscription plan,
 * including coupon changes.
 */
export const ControlSingleConfirmationFormGroup: React.FC<Props> = ({
  values,
  formattedexpirationDate,
  formattedPrice,
  formattedDiscountPrice,
  formattedInstallmentPrice,
  showPlanName,
  setFieldValue
}) => {
  const [couponsCache, setCouponsCache] = useState([]);
  const [loading, setLoading] = useState(false);

  const validateCoupon = useRef(
    debounce(
      async (couponCode: string, cache: any[], setCache: (Array) => void, setLoading: (boolean) => void) => {
        setLoading(true);
        const cached = cache.find((discount) => discount.couponCode === couponCode);

        if (!cached) {
          try {
            const response = await fetch(
              paths.api.validateCoupon(values.orderItems[0].purchasable.id, couponCode, true),
              {
                method: 'GET'
              }
            );

            if (!response.couponCode) {
              setFieldValue('discount', null);
              setCache([...cache, { couponCode: couponCode, discount: null }]);
            } else {
              setFieldValue('discount', response);
              setCache([...cache, { couponCode: couponCode, discount: response }]);
            }
            setLoading(false);
          } catch (err) {
            setLoading(false);
          }
        } else {
          setFieldValue('discount', cached.discount);
          setLoading(false);
        }
      },
      1500
    )
  ).current;

  const handleValidate = () => {
    validateCoupon(values.couponCode, couponsCache, setCouponsCache, setLoading);
  };

  return (
    <>
      <h4 className="u-margin-V-n inline-form-right">
        {showPlanName && (
          <strong className="subscription_plan_name">{values.orderItems[0].purchasable.name}</strong>
        )}
      </h4>
      <p className="inline-form-right-help extra-bottom-margin">
        <small className="text-gray-light">Expires on {formattedexpirationDate}</small>
      </p>

      {values.discount?.compoundPrice ? (
        <div className="subscription_price inline-form-right extra-bottom-margin">
          <table>
            <tbody>
              {values.discount.compoundPrice.map((price, index) => (
                <tr key={`price_${index}`}>
                  <td className={'u-padding-T-xs'}>{price.name}</td>
                  <td className="u-padding-T-xs u-padding-L-xs">{formatPrice(price.price)}</td>
                  {price.fullPrice && price.fullPrice != price.price && (
                    <td className="u-padding-T-xs u-padding-L-xs">
                      <small>
                        <span className={style.strike}>{formatPrice(price.fullPrice)}</span> with{' '}
                        {values.discount.discountDescription}
                      </small>
                    </td>
                  )}
                </tr>
              ))}
              <tr className="total-price-line">
                <td className="u-padding-T-xs"></td>
              </tr>
              <tr className="total-price">
                <td className="u-padding-T-xs u-padding-B-xs">Total:</td>
                <td className="u-padding-T-xs u-padding-B-xs u-padding-L-xs">{formattedDiscountPrice}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="subscription_price inline-form-right extra-bottom-margin">
          <h4>
            <strong>{formattedDiscountPrice}</strong>
            {values.discount && (
              <small>
                {' '}
                <span className={style.strike}>{formattedPrice}</span> with{' '}
                {values.discount.discountDescription}
              </small>
            )}
          </h4>

          {values.paymentStyle === PaymentStyle.INSTALLMENTS && (
            <p>
              <strong>{formattedInstallmentPrice}</strong>
              <small> today</small>
            </p>
          )}
        </div>
      )}

      <div className={style.spinner}>
        <FieldInput name="couponCode" type="input" label="Promo Code" onKeyUp={handleValidate} />
        {loading && <FontAwesomeIcon icon="spinner" className="fa-pulse u-margin-H-xs" />}
      </div>
    </>
  );
};

export default ControlSingleConfirmationFormGroup;
